<template>
	<div id="net-returns-body">
		<div class="net-returns-tab-nav tabs hidden-sm hidden-xs">
			<!-- First tab-->
			<div class="tab" :class="{ active: isMonthlyActive }" @click="changeActiveTab('monthly')">
				<h3 class="header">
					Month-End Returns
					<span class="as-of">as of {{ monthlyAsOfDate }}</span>
				</h3>
			</div>
			<!-- Second tab-->
			<div class="tab" :class="{ active: isQuarterlyActive }" @click="changeActiveTab('quarterly')">
				<h3 class="header">
					Quarter-End Returns
					<span class="as-of">as of {{ quarterlyAsOfDate }}</span>
				</h3>
			</div>
			<!--Annual returns link-->
			<a class="annual-link large modal-link" @click.stop="openAnnualChart()">
				<div class="arrow-wrapper">
					<i class="icon icon-arrow-right" />
				</div>ANNUAL RETURNS CHART
			</a>
		</div>
		<!-- tabs share a view, where only the source of the chart changes -->
		<div class="hidden-xs hidden-sm">
			<h4 v-if="showSubHeader">
				Returns at NAV
				<slot name="superscript5" />
			</h4>
			<ReadMoreLink />
			<div class="chart-legend">
				<span>
					<div class="square bg-brand-primary" />
					{{ primaryLabel }}
				</span>
				<span v-if="secondaryLabel">
					<div class="square bg-brand-secondary" />
					{{ secondaryLabel }}
				</span>
				<span v-if="isQuarterlyActive">
					<div class="square bg-neutral-primary" />
					{{ quarterlyIndexName }}
					<slot name="quarterlyIndexSuperscript" />
					<slot name="quarterlyIndex" />
				</span>
				<span v-else>
					<div class="square bg-neutral-primary" />
					{{ monthlyIndexName }}
					<slot name="monthlyIndexSuperscript" />
					<slot name="monthlyIndex" />
				</span>
			</div>
			<div class="net-returns-chart">
				<div v-if="isQuarterlyActive">
					<img :src="quarterlyChart" >
				</div>
				<div v-else>
					<img :src="monthlyChart" >
				</div>
			</div>
		</div>
		<!--First accordion item-->
		<div
			class="row visible-xs visible-sm first-item"
			:class="{ active: isMonthlyActive }"
			@click="changeActiveTab('monthly')"
		>
			<div class="col-md-12">
				<div class="expand-collapse" data-viewname="growth-of-10k">
					<div :class="[isMonthlyActive ? 'white-arrow-down' : 'white-arrow-right']" />
					<h3>
						Month-End Returns
						<span class="as-of">as of {{ monthlyAsOfDate }}</span>
					</h3>
				</div>
			</div>
		</div>
		<!-- First accordian view-->
		<div v-if="isMonthlyActive" class="visible-xs visible-sm">
			<h4 class="menu">
				Returns at NAV
				<slot name="superscript4" />
			</h4>
			<ReadMoreLink />
			<div class="chart-legend">
				<span class="legend-item">
					<div class="square bg-brand-primary" />
					{{ primaryLabel }}
				</span>
				<span v-if="secondaryLabel" class="legend-item">
					<div class="square bg-brand-secondary" />
					{{ secondaryLabel }}
				</span>
				<span class="legend-item">
					<div class="square bg-neutral-primary" />
					{{ monthlyIndexName }}
					<slot name="monthlyIndexSuperscript" />
					<slot name="monthlyIndex" />
				</span>
			</div>
			<div class="net-returns-chart">
				<img :src="monthlyChart" >
			</div>
			<a class="annual-link" @click.stop="openAnnualChart()">
				<div class="arrow-wrapper">
					<i class="icon icon-arrow-right" />
				</div>ANNUAL RETURNS CHART
			</a>
		</div>
		<!--Second accordion item-->
		<div
			class="row visible-xs visible-sm first-item"
			:class="{ active: isQuarterlyActive }"
			@click="changeActiveTab('quarterly')"
		>
			<div class="col-md-12">
				<div class="expand-collapse" data-viewname="growth-of-10k">
					<div :class="[isQuarterlyActive ? 'white-arrow-down' : 'white-arrow-right']" />
					<h3>
						Quarter-End Returns
						<span class="as-of">as of {{ quarterlyAsOfDate }}</span>
					</h3>
				</div>
			</div>
		</div>
		<!-- Second accordian view-->
		<div v-if="isQuarterlyActive" class="visible-xs visible-sm">
			<h4 class="menu">
				Returns at NAV
				<slot name="superscript4" />
			</h4>
			<ReadMoreLink />
			<div class="chart-legend">
				<span class="legend-item">
					<div class="square bg-brand-primary" />
					{{ primaryLabel }}
				</span>
				<span v-if="secondaryLabel" class="legend-item">
					<div class="square bg-brand-secondary" />
					{{ secondaryLabel }}
				</span>
				<span class="legend-item">
					<div class="square bg-neutral-primary" />
					{{ quarterlyIndexName }}
					<slot name="quarterlyIndexSuperscript" />
					<slot name="quarterlyIndex" />
				</span>
			</div>
			<div class="net-returns-chart">
				<img :src="quarterlyChart" >
			</div>
			<a class="annual-link" @click.stop="openAnnualChart()">
				<div class="arrow-wrapper">
					<i class="icon icon-arrow-right" />
				</div>ANNUAL RETURNS CHART
			</a>
		</div>
	</div>
</template>

<script>
import ReadMoreLink from '_components_/Shared/ReadMoreLink';
export default {
	name: 'GrossNetIndexReturns',
	components: {
		ReadMoreLink
	},
	props: {
		/**
		 * Widget data displayed as a array of objetcs with label and data keys
		 */
		widgetData: {
			type: Object,
			required: true
		}
	},
	data() {
		this.isMonthlyActive = true;
		this.isQuarterlyActive = false;
		return {
			isMonthlyActive: this.isMonthlyActive,
			isQuarterlyActive: this.isQuarterlyActive
		};
	},
	computed: {
		componentData() {
			return this.widgetData || {};
		},
		primaryLabel() {
			return this.componentData.primaryLabel || '--';
		},
		//secondary label is optional and added by v-if - if missing default to empty string
		secondaryLabel() {
			return this.componentData.secondaryLabel || '';
		},
		monthlyAsOfDate() {
			return this.componentData.monthly?.asOfDate ?? '';
		},
		monthlyChart() {
			return this.componentData.monthly?.chart ?? '';
		},
		monthlyIndexName() {
			return this.componentData.monthly?.cgIndexName ?? '--';
		},
		quarterlyAsOfDate() {
			return this.componentData.quarterly?.asOfDate ?? '';
		},
		quarterlyChart() {
			return this.componentData.quarterly?.chart ?? '';
		},
		quarterlyIndexName() {
			return this.componentData.quarterly?.cgIndexName ?? '--';
		},
		showSubHeader() {
			return this.componentData.showSubHeader || false;
		}
	},
	methods: {
		changeActiveTab(activeTab) {
			if (activeTab === 'monthly') {
				this.isMonthlyActive = true;
				this.isQuarterlyActive = false;
			}
			if (activeTab === 'quarterly') {
				this.isQuarterlyActive = true;
				this.isMonthlyActive = false;
			}
		},
		openAnnualChart() {
			const data = { modal: 'charts modal', activeTab: 'annual' };
			this.$emit('openModal', data);
		}
	}
};
</script>

<style lang="less" scoped>
@import '~_css_/tabs.css';
#net-returns-body {
	margin-bottom: 35px;
	h4 {
		font-size: 14px;
		font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
		font-weight: 400;
	}
	.net-returns-tab-nav {
		border-top: 1px solid rgb(212, 212, 212);
		margin-bottom: -10px;
		display: flex;
		margin-right: 5px;
		.disclosure-link {
			font-size: 14px;
			font-weight: 700;
		}
	}
	.as-of {
		font-family: 'AvenirNextLTW01-Regular', Arial;
		font-size: 14px;
	}
	.annual-link {
		color: #1d5799;
		display: block;
		font-family: 'Avenir Next LT W01 Demi', Arial, sans-serif;
		font-size: 14px;
		margin-top: 10px;
		margin-left: auto;
		margin-bottom: 30px;
		text-transform: uppercase;
		.arrow-wrapper {
			display: inline-block;
			color: #878a96;
			.icon {
				line-height: 15px;
			}
		}
		&.large {
			float: right;
		}
	}
	.chart-legend {
		display: inline-block;
		margin-top: 15px;
		span {
			font-size: 12px;
			color: #666;
			font-family: 'AvenirNextLTW01-Regular', Arial;
			&:not(last-of-type) {
				margin-right: 15px;
			}
		}
	}
	.net-returns-chart {
		margin-top: 5px;
		img {
			width: 100%;
		}
	}
	@media (max-width: 768px) {
		.legend-item {
			display: block;
			margin: 0;
		}
		.menu {
			margin-top: 20px;
		}
	}
	@media (max-width: 480px) {
		.as-of {
			display: block;
			margin-left: 16px;
		}
	}
}
</style>
